// FILE: src/components/admin/staffBits.jsx
// Shared pieces of the Staff & roles pages (RBAC): the section tabs, a staff
// account's status and DDI registration as icon + words, and the wording of
// the role refusals that carry extra details.
import React from "react";
import { Stack, Typography } from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import MarkEmailUnreadOutlinedIcon from "@mui/icons-material/MarkEmailUnreadOutlined";
import VerifiedUserOutlinedIcon from "@mui/icons-material/VerifiedUserOutlined";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import SubNav from "./SubNav";
import { StatusChip } from "../ui";
import { errorPayload } from "./apiError";
import { errorCode, errorMessage } from "../../i18n/errors";
import usePermissions from "../../hooks/usePermissions";

/** Tabs between the Staff and Roles pages (each shown only when the role allows it). */
export function StaffRolesNav({ current }) {
  const { can } = usePermissions();
  const items = [
    can("staff.manage") && { label: "Staff", path: "/admin/staff" },
    can({ anyOf: ["roles.manage", "staff.manage"] }) && { label: "Roles", path: "/admin/roles" }
  ].filter(Boolean);
  if (items.length < 2) return null;
  return <SubNav label="Staff and roles" items={items} current={current} />;
}

/** Invited / Locked / Active, as icon + words. */
export function staffStatus(user) {
  if (user?.accountLocked) return { label: "Locked", tone: "error", icon: <LockOutlinedIcon /> };
  if (user?.invited) return { label: "Invitation sent", tone: "info", icon: <MarkEmailUnreadOutlinedIcon /> };
  return { label: "Active", tone: "success", icon: <CheckCircleOutlineIcon /> };
}

export function StaffStatusChip({ user }) {
  const s = staffStatus(user);
  return <StatusChip label={s.label} tone={s.tone} icon={s.icon} />;
}

/** "Yes, DDI-0042" / "No" with an icon. */
export function DdiText({ user }) {
  const value = (user?.staffRegistration || "").trim();
  return (
    <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
      {value ? (
        <VerifiedUserOutlinedIcon aria-hidden sx={{ color: "primary.main" }} />
      ) : (
        <RemoveCircleOutlineIcon aria-hidden sx={{ color: "text.secondary" }} />
      )}
      <Typography sx={{ overflowWrap: "anywhere" }}>{value ? `Yes, ${value}` : "No"}</Typography>
    </Stack>
  );
}

/** Words for a refused role or staff request, using the details some refusals carry. */
export function rbacErrorText(err) {
  const code = errorCode(err);
  const body = errorPayload(err);
  if (code === "PERMISSION_DENIED" && body.reason === "GRANTS_MORE_THAN_YOURS") {
    return "You can't give out permissions your own role doesn't have. Ask an administrator to do this.";
  }
  if (code === "ROLE_IN_USE" && Number.isFinite(Number(body.members))) {
    const n = Number(body.members);
    return `${n} staff ${n === 1 ? "member still has" : "members still have"} this role. Give ${n === 1 ? "them" : "each of them"} another role first, then delete it.`;
  }
  return errorMessage(err);
}

/** A role-form problem from the server (VALIDATION_FAILED problems) in words. */
export function roleProblemText(problem) {
  if (!problem) return "";
  if (problem.field === "name") {
    if (problem.problem === "taken") return "Another role already has this name. Choose a different one.";
    if (problem.problem === "required") return "Enter a name for the role.";
    return "Use 2 to 60 characters for the name.";
  }
  if (problem.field === "description") return "Use 500 characters or fewer for the description.";
  if (String(problem.field).startsWith("permissions")) return "One of the permissions isn't recognised. Reload the page and choose again.";
  return "Some details are missing or not in the right format.";
}
